<template>
  <div
    v-if="lineItem"
    class="bg-white p-4 mb-8 box-shadow rounded border-top-tertiary"
  >
    <div class="d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <EquipmentRequestIcon :status="lineItem.equipment_request_status" />
        <h5 class="font-bold ml-1">
          {{ lineItem.unit_name }}
          <span v-if="totalQty > 1">- Qty ({{ totalQty }})</span>
        </h5>
      </div>
      <SeraButton
        :icon="`fa-solid fa-chevron-${open ? 'up' : 'down'}`"
        class="font-large"
        @click="toggleOpen"
      />
    </div>
    <Transition name="slide-up-fade">
      <div v-if="open" class="mt-2">
        <div v-for="(links, key) in groupedLinks" :key="key">
          <div
            v-if="Object.keys(groupedLinks).length > 1"
            class="mt-4 qty-label"
          >
            <span v-if="!isNaN(key)">Qty #{{ key }} of {{ totalQty }}</span>
          </div>
          <div v-for="link in links" :key="link.id" class="py-2 rounded">
            <EquipmentRequestForm
              :equipment="equipmentForLink(link.equipment_type.id)"
              :equipment-request-status="lineItem.equipment_request_status"
              :equipment-link="link"
              @update="updateLink"
              @clear-selection="clearSelection(link)"
              @add-replace-click="toggleReplaceModal(link)"
            >
              <template #equipmentCard>
                <WarningMessage v-if="hasMultipleOfTheSameEquipment(link)">
                  <span>This equipment has already been used.</span>
                </WarningMessage>
                <EquipmentCard
                  v-bind="link.equipment"
                  :equipment-type-name="equipment.equipment_type_name"
                  :address="fullAddress(link.equipment.address)"
                  :service-history="link.equipment.service_history"
                  :show-service-history="equipmentTypesEnabled"
                  class="mb-4"
                  :data-cy="`equipment-card-${link.equipment.id}`"
                  default-collapsed
                  @edit="toggleEditEquipmentModal(link.equipment)"
                >
                  <template #componentsTable>
                    <EquipmentComponentsTable
                      :components="activeComponents(link.equipment.components)"
                    />
                  </template>
                  <template #serviceHistoryTable>
                    <EquipmentServiceHistoryTable
                      :service-history="link.equipment.service_history"
                    />
                  </template>
                </EquipmentCard>
              </template>
            </EquipmentRequestForm>
          </div>
        </div>
        <AdditionalEquipmentRequestForm
          ref="optionalForm"
          class="pt-2 mt-4"
          :equipment="equipment"
          :line-item="lineItem"
          @create-equipment-link="createLink"
          @add-replace-click="toggleReplaceModal(null)"
        />
        <EquipmentReplaceModal
          v-if="replaceModal"
          :address="address"
          :customer="customer"
          :equipment="equipment"
          :equipment-link="currentEquipmentLink"
          @update-link="updateLink"
          @create-link="createLink"
          @hide-modal="toggleReplaceModal(null)"
        />
        <EquipmentModal
          v-if="editEquipmentModal"
          key="editModal"
          :equipment-id="currentEquipment.id"
          :customer-id="customer.id"
          :address="address"
          @hide-modal="toggleEditEquipmentModal(null)"
          @save-success="refreshInvoice"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import groupBy from "lodash/groupBy";
import WarningMessage from "serviceshift-ui/components/General/WarningMessage.vue";
import { mapActions, mapState } from "vuex";

import SeraButton from "@/components/Button.vue";
import Transition from "@/components/Transition.vue";
import EquipmentComponentsTable from "@/ComponentTypes/EquipmentComponentsTable.vue";
import EquipmentModal from "@/Equipment/EquipmentModal.vue";
import EquipmentReplaceModal from "@/Equipment/EquipmentReplaceModal.vue";
import EquipmentServiceHistoryTable from "@/Equipment/EquipmentServiceHistoryTable.vue";
import { fullAddress } from "@/mixins";
import AdditionalEquipmentRequestForm from "./AdditionalEquipmentRequestForm.vue";

export default {
  components: {
    EquipmentComponentsTable,
    EquipmentModal,
    EquipmentReplaceModal,
    SeraButton,
    Transition,
    EquipmentServiceHistoryTable,
    AdditionalEquipmentRequestForm,
    WarningMessage
  },
  props: {
    lineItem: {
      type: Object,
      required: true
    },
    equipment: {
      type: Array,
      default: () => []
    },
    address: {
      type: Object,
      default: null
    },
    customer: {
      type: Object,
      default: null
    }
  },
  emits: ["refresh-equipment", "refresh-invoice"],
  data() {
    return {
      additionalEquipmentType: null,
      replaceModal: false,
      addModal: false,
      currentEquipmentLink: null,
      currentEquipment: null,
      editEquipmentModal: false,
      open: false
    };
  },
  computed: {
    ...mapState({
      equipmentTypes: "equipmentTypes",
      currentUser: "currentUser",
      genericEquipmentTypeId({ equipmentTypes }) {
        return (equipmentTypes.find((type) => type.is_default) || {}).id;
      },
      equipmentTypesEnabled({ tenant }) {
        if (!tenant) return false;
        return tenant.feature_flags.equipment_types;
      }
    }),
    groupedLinks() {
      return groupBy(this.lineItem.equipment_links, "unit_number");
    },
    totalQty() {
      return Object.keys(this.groupedLinks).reduce((total, idx) => {
        if (!isNaN(parseInt(idx))) total++;
        return total;
      }, 0);
    }
  },
  mounted() {
    this.setOpen();
  },
  methods: {
    ...mapActions([
      "createEquipmentLink",
      "updateEquipmentLink",
      "destroyEquipmentLink",
      "setLineItemEquipmentLink"
    ]),
    fullAddress,
    hasMultipleOfTheSameEquipment(linkToCheck) {
      const sameEquipmentList = this.lineItem.equipment_links.filter(
        (equipmentLink) => {
          if (!equipmentLink.equipment || !linkToCheck.equipment) return false;
          return equipmentLink.equipment.id === linkToCheck.equipment.id;
        }
      );
      if (sameEquipmentList.length <= 1) return false;
      return true;
    },
    setOpen() {
      this.open = this.lineItem.equipment_links.some(
        (link) => link.equipment || link.status !== "optional"
      );
    },
    equipmentForLink(equipmentTypeId) {
      return this.equipment.filter(
        (eq) =>
          eq.equipment_type_id === equipmentTypeId ||
          this.isGenericEquipment(eq)
      );
    },
    isGenericEquipment(equipment) {
      return equipment.equipment_type_id === this.genericEquipmentTypeId;
    },
    async createLink(data) {
      try {
        await this.createEquipmentLink({
          link: this.lineItem.links.equipment_link_create,
          data
        });
        const form = this.$refs.optionalForm;
        form.resetForm();
        this.$emit("refresh-equipment");
      } catch (e) {
        console.error(e);
      }
    },
    async clearSelection(link) {
      try {
        await this.destroyEquipmentLink({
          link: link.links.equipment_link_destroy,
          linkId: link.id
        });
        window.notify.success("Equipment request cleared!");
      } catch (e) {
        console.error(e);
      }
    },
    async updateLink(link) {
      try {
        await this.updateEquipmentLink({
          link: link.links.equipment_link_update,
          data: link
        });
        window.notify.success("Equipment request updated!");
        this.$emit("refresh-equipment");
      } catch (e) {
        console.error(e);
      }
    },
    toggleReplaceModal(equipmentLink) {
      this.currentEquipmentLink = equipmentLink;
      this.replaceModal = !this.replaceModal;
    },
    activeComponents(components) {
      return components.filter((comp) => !comp.archived);
    },
    toggleEditEquipmentModal(equipment) {
      this.currentEquipment = equipment;
      this.editEquipmentModal = !this.editEquipmentModal;
    },
    refreshInvoice() {
      // refreshes invoice to get updated line item equipment links
      // after editing equipment
      this.$emit("refresh-invoice");
    },
    toggleOpen() {
      this.open = !this.open;
    }
  }
};
</script>

<style scoped>
.border-top-tertiary {
  border-top: 5px solid var(--tertiary-color);
}
.font-large {
  font-size: 1.3em;
}
:deep(.card-header) {
  border-bottom: none !important;
}
.qty-label {
  font-weight: bold;
  font-size: 1.2em;
}
:deep(.equipment-request-header) {
  font-weight: bold;
}
</style>
